import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { trackSignUp } from '../utils/analytics';
import { metaCompleteRegistration } from '../utils/metaAppEvents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { authAPI } from '../utils/api';
import { redeemPendingReferral } from '../utils/referral';
import { COUNTRIES } from '../utils/constants';
import { toast } from 'sonner';
import { normalizePhoneNumber } from '../utils/phoneUtils';
import BackButton from '../components/BackButton';
import OTPVerification from '../components/OTPVerification';
import Seo from '../components/Seo';
import AppleSignInButton from '../components/AppleSignInButton';
import { isIosApp } from '../utils/platform';

export const Auth = () => {
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgot, setIsForgot] = useState(false);
  const [showOTPScreen, setShowOTPScreen] = useState(false);
  const [pendingPhone, setPendingPhone] = useState('');
  const [pendingMode, setPendingMode] = useState('signup'); // 'signup' | 'setpin'
  const [pendingSignup, setPendingSignup] = useState(null);
  const [pendingPin, setPendingPin] = useState('');
  const [formData, setFormData] = useState({
    phoneNumber: '',
    name: '',
    country: 'USA',
    currency: 'USD',
    countryCode: 'USA',
    pin: '',
    confirmPin: '',
    acceptTerms: false,
  });
  const [loading, setLoading] = useState(false);

  // Pre-fill phone + name + country from the WhatsApp welcome link (?phone=+1555...&name=Ali)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rawPhone = params.get('phone');
    const rawName = params.get('name');
    if (!rawPhone && !rawName) return;
    if (rawName) {
      setFormData((d) => ({ ...d, name: rawName.trim() }));
    }
    if (rawPhone) {
      const phone = rawPhone.trim();
      const match = [...COUNTRIES]
        .sort((a, b) => b.dialCode.length - a.dialCode.length)
        .find((c) => phone.startsWith(c.dialCode));
      if (match) {
        const local = phone.slice(match.dialCode.length).replace(/\D/g, '');
        setFormData((d) => ({ ...d, phoneNumber: local, country: match.code, countryCode: match.code, currency: match.currency }));
      } else {
        setFormData((d) => ({ ...d, phoneNumber: phone.replace(/[^\d]/g, '') }));
      }
    }
    setIsSignUp(true);
  }, []);

  const handleCountryChange = (countryCode) => {
    const country = COUNTRIES.find((c) => c.code === countryCode);
    setFormData({ ...formData, country: countryCode, countryCode, currency: country.currency });
  };

  const getNormalizedPhone = () => {
    const selectedCountry = COUNTRIES.find((c) => c.code === formData.countryCode);
    return normalizePhoneNumber(formData.phoneNumber, selectedCountry?.dialCode);
  };

  const validatePinInput = (pin) => /^\d{4,6}$/.test(pin);

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!formData.phoneNumber) return toast.error('Please enter your phone number');
    if (!formData.pin) return toast.error('Please enter your PIN');

    const normalizedPhone = getNormalizedPhone();
    setLoading(true);
    try {
      const res = await authAPI.login({ phone_number: normalizedPhone, pin: formData.pin });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      localStorage.setItem('lastActive', Date.now().toString());
      toast.success('Welcome back!');
      setTimeout(() => navigate('/dashboard'), 400);
    } catch (error) {
      const status = error.response?.status;
      if (status === 404) {
        toast.error('Account not found. Please sign up first.');
        setIsSignUp(true);
      } else if (status === 403) {
        toast.error("No PIN set yet. Tap 'Forgot / Set PIN' to create one.");
        setIsForgot(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!formData.phoneNumber) return toast.error('Please enter your phone number');
    if (!formData.name) return toast.error('Please enter your name');
    if (!validatePinInput(formData.pin)) return toast.error('PIN must be 4 to 6 digits');
    if (formData.pin !== formData.confirmPin) return toast.error('PINs do not match');
    if (!formData.acceptTerms) return toast.error('Please accept the Terms of Service and Privacy Policy to continue');

    const normalizedPhone = getNormalizedPhone();
    setLoading(true);
    try {
      try {
        const existing = await authAPI.getUser(normalizedPhone);
        if (existing.data.has_pin) {
          toast.error('Account already exists. Please sign in.');
          setIsSignUp(false);
          setLoading(false);
          return;
        }
      } catch (err) {
        if (err.response?.status !== 404) throw err;
      }
      await authAPI.sendOTP(normalizedPhone);
      setPendingPhone(normalizedPhone);
      setPendingSignup({
        phone_number: normalizedPhone,
        name: formData.name,
        country: formData.country,
        currency: formData.currency,
        pin: formData.pin,
        accept_terms: true,
      });
      setPendingMode('signup');
      setShowOTPScreen(true);
      toast.success('We sent a verification code to your WhatsApp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send code. Make sure your number has WhatsApp.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPin = async (e) => {
    e.preventDefault();
    if (!formData.phoneNumber) return toast.error('Please enter your phone number');
    if (!validatePinInput(formData.pin)) return toast.error('New PIN must be 4 to 6 digits');
    if (formData.pin !== formData.confirmPin) return toast.error('PINs do not match');

    const normalizedPhone = getNormalizedPhone();
    setLoading(true);
    try {
      await authAPI.sendOTP(normalizedPhone);
      setPendingPhone(normalizedPhone);
      setPendingPin(formData.pin);
      setPendingMode('setpin');
      setShowOTPScreen(true);
      toast.success('We sent a verification code to your WhatsApp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send code. Make sure your number has WhatsApp.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerified = async (code) => {
    if (pendingMode === 'signup' && pendingSignup) {
      const res = await authAPI.register({ ...pendingSignup, otp: code });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      localStorage.setItem('lastActive', Date.now().toString());
      trackSignUp('phone');
      metaCompleteRegistration('phone');
      toast.success('Account created successfully!');
      const ref = await redeemPendingReferral();
      if (ref?.user) {
        localStorage.setItem('user', JSON.stringify(ref.user));
        toast.success('🎉 Referral applied — you got +10 bonus receipt scans!');
      }
      setTimeout(() => navigate('/welcome'), 400);
    } else {
      const res = await authAPI.setPin({ phone_number: pendingPhone, otp: code, pin: pendingPin });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      localStorage.setItem('lastActive', Date.now().toString());
      toast.success('PIN set! You are now signed in.');
      setTimeout(() => navigate('/dashboard'), 400);
    }
  };

  const handleBackFromOTP = () => {
    setShowOTPScreen(false);
    setPendingPhone('');
    setPendingSignup(null);
    setPendingPin('');
  };

  if (showOTPScreen) {
    return (
      <OTPVerification phoneNumber={pendingPhone} onVerified={handleOTPVerified} onBack={handleBackFromOTP} />
    );
  }

  const mode = isForgot ? 'forgot' : isSignUp ? 'signup' : 'signin';
  const handleSubmit = mode === 'forgot' ? handleForgotPin : mode === 'signup' ? handleSignUp : handleSignIn;
  const dialCode = COUNTRIES.find((c) => c.code === formData.countryCode)?.dialCode || '+1';

  const switchMode = (m) => {
    setIsSignUp(m === 'signup');
    setIsForgot(m === 'forgot');
    setFormData((d) => ({ ...d, pin: '', confirmPin: '' }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#D1FAE5] flex items-center justify-center px-4">
      <Seo title="Sign in or create your account · MoneyFeed" description="Sign in to MoneyFeed or create your account to do your bookkeeping on WhatsApp." path="/auth" noindex />
      <BackButton to="/" label="Back to Home" variant="home" />

      <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 max-w-md w-full">
        {isIosApp() && mode !== 'forgot' && (
          <div className="mb-6" data-testid="ios-apple-signin-block">
            <AppleSignInButton
              onSignedIn={() => navigate('/dashboard')}
              country={formData.country}
              currency={formData.currency}
            />
            <p className="text-xs text-center text-gray-500 mt-2">No phone number needed — connect WhatsApp later to use the bot.</p>
            <div className="flex items-center gap-3 my-5">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs text-gray-400 font-medium">or use phone</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
          </div>
        )}
        {!isForgot && (
          <div className="flex gap-2 mb-8 bg-slate-100 rounded-full p-1">
            <button type="button" onClick={() => switchMode('signin')}
              className={`flex-1 py-2 px-4 rounded-full font-semibold transition-all duration-300 ${!isSignUp ? 'bg-[#064E3B] text-white' : 'bg-transparent text-gray-600 hover:text-[#064E3B]'}`}
              data-testid="signin-tab">Sign In</button>
            <button type="button" onClick={() => switchMode('signup')}
              className={`flex-1 py-2 px-4 rounded-full font-semibold transition-all duration-300 ${isSignUp ? 'bg-[#064E3B] text-white' : 'bg-transparent text-gray-600 hover:text-[#064E3B]'}`}
              data-testid="signup-tab">Sign Up</button>
          </div>
        )}

        <h1 className="text-4xl font-bold text-[#064E3B] mb-2">
          {mode === 'forgot' ? 'Reset PIN' : mode === 'signup' ? 'Create Account' : 'Welcome Back'}
        </h1>
        <p className="text-gray-600 mb-8">
          {mode === 'forgot' ? 'Verify your number to set a new PIN'
            : mode === 'signup' ? 'Create your account to start tracking expenses'
            : 'Sign in with your phone number and PIN'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="countryCode">Country *</Label>
            <Select value={formData.countryCode} onValueChange={handleCountryChange}>
              <SelectTrigger className="mt-2" id="countryCode" data-testid="country-select">
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent className="max-h-80 overflow-hidden">
                <div className="sticky top-0 bg-white p-3 border-b border-gray-200 z-10">
                  <Input placeholder="Type to search (e.g., Pakistan, USA...)"
                    onChange={(e) => {
                      const search = e.target.value.toLowerCase();
                      document.querySelectorAll('[data-country-item]').forEach((item) => {
                        item.style.display = item.textContent.toLowerCase().includes(search) ? 'flex' : 'none';
                      });
                    }}
                    className="h-9 text-sm" autoFocus onClick={(e) => e.stopPropagation()} />
                </div>
                <div className="overflow-y-auto max-h-60 p-1">
                  {COUNTRIES.map((country) => (
                    <SelectItem key={country.code} value={country.code} data-country-item className="cursor-pointer">
                      {country.dialCode} - {country.name}
                    </SelectItem>
                  ))}
                </div>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="phone">Phone Number *</Label>
            <div className="mt-2 flex gap-2">
              <div className="w-20 flex items-center justify-center bg-slate-100 border border-slate-300 rounded-md px-3 text-sm font-semibold text-gray-700">
                {dialCode}
              </div>
              <Input id="phone" data-testid="phone-input" type="tel" placeholder="1234567890"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                className="flex-1" />
            </div>
            <p className="text-xs text-gray-500 mt-1">Enter your number without country code</p>
          </div>

          {mode === 'signup' && (
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input id="name" data-testid="name-input" type="text" placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-2" />
            </div>
          )}

          <div>
            <Label htmlFor="pin">{mode === 'signin' ? 'PIN *' : 'New PIN (4-6 digits) *'}</Label>
            <Input id="pin" data-testid="pin-input" type="password" inputMode="numeric" maxLength={6}
              placeholder="••••" value={formData.pin}
              onChange={(e) => setFormData({ ...formData, pin: e.target.value.replace(/\D/g, '') })}
              className="mt-2 tracking-widest" />
          </div>

          {(mode === 'signup' || mode === 'forgot') && (
            <div>
              <Label htmlFor="confirmPin">Confirm PIN *</Label>
              <Input id="confirmPin" data-testid="confirm-pin-input" type="password" inputMode="numeric" maxLength={6}
                placeholder="••••" value={formData.confirmPin}
                onChange={(e) => setFormData({ ...formData, confirmPin: e.target.value.replace(/\D/g, '') })}
                className="mt-2 tracking-widest" />
            </div>
          )}

          {mode === 'signup' && (
            <div className="flex items-start gap-3 rounded-xl bg-slate-50 border border-slate-200 p-3">
              <Checkbox
                id="accept-terms"
                data-testid="accept-terms-checkbox"
                checked={formData.acceptTerms}
                onCheckedChange={(checked) => setFormData({ ...formData, acceptTerms: checked === true })}
                className="mt-0.5 border-[#064E3B] data-[state=checked]:bg-[#064E3B]"
              />
              <Label htmlFor="accept-terms" className="text-xs text-gray-600 font-normal leading-relaxed cursor-pointer">
                I agree to MoneyFeed's{' '}
                <Link to="/terms" target="_blank" className="text-[#064E3B] font-semibold hover:underline" data-testid="terms-link">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank" className="text-[#064E3B] font-semibold hover:underline" data-testid="privacy-link">Privacy Policy</Link>
                , and consent to receive WhatsApp messages from MoneyFeed (reminders &amp; updates). Reply STOP anytime to opt out.
              </Label>
            </div>
          )}

          <Button type="submit"
            data-testid={mode === 'signup' ? 'signup-btn' : mode === 'forgot' ? 'reset-pin-btn' : 'signin-btn'}
            disabled={loading}
            className="w-full bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300">
            {loading ? 'Please wait...'
              : mode === 'signup' ? 'Create Account'
              : mode === 'forgot' ? 'Send Code & Reset PIN'
              : 'Sign In'}
          </Button>
        </form>

        {mode === 'signin' && (
          <div className="text-center mt-4">
            <button type="button" onClick={() => switchMode('forgot')}
              className="text-sm text-[#064E3B] font-semibold hover:underline" data-testid="forgot-pin-link">
              Forgot / Set PIN?
            </button>
          </div>
        )}

        <div className="text-center text-sm text-gray-600 mt-6">
          {mode === 'forgot' ? (
            <button type="button" onClick={() => switchMode('signin')} className="text-[#064E3B] font-semibold hover:underline">
              ← Back to Sign In
            </button>
          ) : isSignUp ? (
            <p>Already have an account?{' '}
              <button type="button" onClick={() => switchMode('signin')} className="text-[#064E3B] font-semibold hover:underline">Sign In</button>
            </p>
          ) : (
            <p>Don't have an account?{' '}
              <button type="button" onClick={() => switchMode('signup')} className="text-[#064E3B] font-semibold hover:underline">Sign Up</button>
            </p>
          )}
        </div>

        {mode !== 'signup' && (
          <p className="text-center text-xs text-gray-500 mt-4">By continuing, you agree to our{' '}
            <Link to="/terms" className="text-[#064E3B] hover:underline">Terms</Link> and{' '}
            <Link to="/privacy" className="text-[#064E3B] hover:underline">Privacy Policy</Link>.
          </p>
        )}
      </div>
    </div>
  );
};

export default Auth;
